import { Component, ElementRef, viewChild, viewChildren, input, effect, HostListener, inject, signal, computed } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { measureWidestXLabel, truncateSvgText } from '../../../helpers/graphs/svg-text.helper';

// Minimum room between neighbouring x-axis labels before they are shortened
const X_LABEL_GAP = 8;
const X_LABEL_MIN_WIDTH = 14;
// Breathing room between a centred edge label and the plot edge
const X_LABEL_EDGE_GAP = 2;
// A legend item counts as in view from this ratio; the slack absorbs sub-pixel layout
const LEGEND_FULLY_VISIBLE_RATIO = 0.99;
const LEGEND_EDGE_TOLERANCE_PX = 1;

export interface MultiLineChartDataPoint {
  [key: string]: string | number;
}

export interface MultiLineChartSeries {
  key: string;
  color: string;
  label?: string;
  showDots?: boolean; // defaults to true if not specified
}

export interface LegendItem {
  legend: string;
  color: string;
}

@Component({
  selector: 'app-multi-line-chart',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  templateUrl: './multi-line-chart.component.html',
  styleUrl: './multi-line-chart.component.scss'
})
export class MultiLineChartComponent {
  private readonly transloco = inject(TranslocoService);
  readonly chartContainer = viewChild<ElementRef>('chart');
  readonly legendTrack = viewChild<ElementRef<HTMLElement>>('legendTrack');
  readonly legendItems = viewChildren<ElementRef<HTMLElement>>('legendItem');

  readonly canScrollLegendPrev = signal(false);
  readonly canScrollLegendNext = signal(false);
  readonly legendOverflows = computed(() => this.canScrollLegendPrev() || this.canScrollLegendNext());

  public data = input<MultiLineChartDataPoint[]>([]);
  public series = input<MultiLineChartSeries[]>([]);
  public legend = input<LegendItem[]>([]);
  public xAxisKey = input<string>('x');
  public chartHeight = input<number>(175);
  public yTicks = input<number[]>([100, 80, 60, 40, 20, 0]);
  public xPadding = input<number>(0.2);
  public xAxisFormat = input<((value: any) => string) | undefined>(undefined);
  public yAxisFormat = input<((value: any) => string) | undefined>(undefined);
  public margin = input({ top: 20, right: 10, bottom: 30, left: 0 });
  public tooltipFormat = input<((value: any) => string) | undefined>(undefined);
  // Keeps the legend on one row that scrolls sideways instead of wrapping
  public scrollableLegend = input<boolean>(false);

  constructor() {
    effect(() => {
      if (this.data()?.length && this.chartContainer()?.nativeElement && this.series()?.length > 0) {
        this.createChart();
      }
    });

    effect((onCleanup) => {
      const track = this.legendTrack()?.nativeElement;
      const items = this.legendItems();
      if (!this.scrollableLegend() || !track || !items.length) return;

      // New makes start from the beginning of the strip
      track.scrollLeft = 0;

      // Only the ends decide whether there is anything to scroll to
      const first = items[0].nativeElement;
      const last = items[items.length - 1].nativeElement;
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const isCutOff = entry.intersectionRatio < LEGEND_FULLY_VISIBLE_RATIO;
          if (entry.target === first) this.canScrollLegendPrev.set(isCutOff);
          if (entry.target === last) this.canScrollLegendNext.set(isCutOff);
        });
      }, { root: track, threshold: [0, LEGEND_FULLY_VISIBLE_RATIO] });

      observer.observe(first);
      observer.observe(last);
      onCleanup(() => observer.disconnect());
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.createChart();
  }

  // Next brings the first make cut off on the right to the front. Previous shows the
  // make cut off on the left as the last one in view, starting the page on a whole make.
  scrollLegend(direction: -1 | 1): void {
    const track = this.legendTrack()?.nativeElement;
    if (!track) return;

    const viewStart = track.scrollLeft;
    const viewEnd = viewStart + track.clientWidth;
    const contentLeft = track.getBoundingClientRect().left - viewStart;
    const bounds = this.legendItems().map(item => {
      const rect = item.nativeElement.getBoundingClientRect();
      return { start: rect.left - contentLeft, end: rect.right - contentLeft };
    });

    let target = 0;
    if (direction === 1) {
      const next = bounds.find(b => b.end > viewEnd + LEGEND_EDGE_TOLERANCE_PX);
      target = next ? next.start : viewEnd;
    } else {
      const previous = [...bounds].reverse().find(b => b.start < viewStart - LEGEND_EDGE_TOLERANCE_PX);
      if (previous) {
        target = bounds.find(b => b.start >= previous.end - track.clientWidth)?.start ?? 0;
      }
    }

    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  // Lets a mouse wheel slide the legend; at either end the page keeps the wheel
  onLegendWheel(event: WheelEvent): void {
    const track = this.legendTrack()?.nativeElement;
    if (!track) return;

    // Trackpads already send horizontal deltas; only vertical intent needs translating
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    // scrollWidth is rounded, so the real end can sit a fraction of a pixel short of it
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (!delta || maxScroll <= 0) return;
    const atStart = track.scrollLeft <= LEGEND_EDGE_TOLERANCE_PX;
    const atEnd = track.scrollLeft >= maxScroll - LEGEND_EDGE_TOLERANCE_PX;
    if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

    track.scrollLeft = Math.min(maxScroll, Math.max(0, track.scrollLeft + delta));
    event.preventDefault();
  }

  private createChart(): void {
    const data = this.data();
    const series = this.series();
    const xAxisKey = this.xAxisKey();
    const chartHeight = this.chartHeight();
    const margin = this.margin();
    const yTicks = this.yTicks();
    const tooltipFormat = this.tooltipFormat();

    if (!data || data.length === 0 || !series || series.length === 0) {
      return;
    }

    const element = this.chartContainer()?.nativeElement;
    if (!element) return;

    const boundingRect = element.getBoundingClientRect();
    const availableWidth = boundingRect.width - margin.left - margin.right;
    const height = chartHeight - margin.top - margin.bottom;

    // Clear existing chart
    d3.select(element).select('svg').remove();
    d3.select(element).select('.tooltip').remove();

    // Create temporary SVG to measure y-axis labels
    const tempSvg = d3.select(element)
      .append('svg')
      .attr('width', availableWidth)
      .attr('height', chartHeight);

    const tempPlot = tempSvg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const yAxisFormat = this.yAxisFormat();
    const yDomain = [0, Math.max(...yTicks)];
    const tempYAxisWithFormat = yAxisFormat
      ? d3.axisLeft(d3.scaleLinear().domain(yDomain).range([height, 0])).tickValues(yTicks).tickFormat(yAxisFormat as any)
      : d3.axisLeft(d3.scaleLinear().domain(yDomain).range([height, 0])).tickValues(yTicks);
    const tempYAxis = tempPlot.append('g').call(tempYAxisWithFormat);
    const maxLabelWidth = Math.ceil(
      Math.max(0, ...(tempYAxis.selectAll('text').nodes() as any[]).map((n: any) => n.getBBox().width))
    );
    const leftPad = maxLabelWidth + 10;
    const width = Math.max(0, availableWidth - leftPad);

    tempSvg.remove();

    // Create tooltip
    const tooltip = d3.select(element)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'fixed')
      .style('background', 'white')
      .style('border', '1px solid #E6E6E6')
      .style('border-radius', '8px')
      .style('padding', '8px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', '1000')
      .style('font-size', '12px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)');

    // Create final SVG
    const svg = d3.select(element)
      .append('svg')
      .attr('width', availableWidth)
      .attr('height', chartHeight)
      .append('g')
      .attr('transform', `translate(${leftPad},${margin.top})`);

    // Create scales
    const x = d3.scalePoint()
      .domain(data.map(d => String(d[xAxisKey])))
      .range([0, width])
      .padding(this.xPadding());

    // Labels are centred under their points, so the end points need half the widest label
    // inside the plot; inset them when the configured padding leaves less room than that.
    const inset = Math.ceil(measureWidestXLabel(svg, x) / 2) + X_LABEL_EDGE_GAP;
    if (x.step() * this.xPadding() < inset) {
      x.range([inset, width - inset]).padding(0);
    }

    const yDomainMax = Math.max(...yTicks);
    const y = d3.scaleLinear()
      .domain([0, yDomainMax])
      .range([height, 0]);

    // Add y-axis with horizontal gridlines (reuse _yAxisFormat from above)
    const yAxisWithFormat = yAxisFormat
      ? d3.axisLeft(y).tickValues(yTicks).tickFormat(yAxisFormat as any).tickSize(-width).tickPadding(10)
      : d3.axisLeft(y).tickValues(yTicks).tickSize(-width).tickPadding(10);

    const yAxisGroup = svg.append('g')
      .attr('class', 'y-axis')
      .call(yAxisWithFormat);

    // Remove axis borders (domain)
    yAxisGroup.select('.domain').remove();

    // Style y-axis gridlines
    yAxisGroup.selectAll('line')
      .style('stroke', '#E0E0E0');

    yAxisGroup.selectAll('text')
      .attr('text-anchor', 'end')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('font-size', '10px')
      .style('fill', '#9FA1A4');

    // Add x-axis
    const xAxis = svg.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x));

    // Remove x-axis domain border
    xAxis.select('.domain').remove();

    // Remove x-axis tick lines
    xAxis.selectAll('line').remove();

    xAxis.selectAll('text')
      .style('font-family', '"Nunito Sans", sans-serif')
      .style('font-size', '10px')
      .style('fill', '#9FA1A4')
      .each((d, i, nodes) => {
        // Same rule as the bar charts: a label may use the distance to its neighbour, minus a gap
        truncateSvgText(nodes[i] as SVGTextElement, Math.max(X_LABEL_MIN_WIDTH, x.step() - X_LABEL_GAP));
      });

    // Create line generator
    const line = d3.line<MultiLineChartDataPoint>()
      .x(d => x(String(d[xAxisKey])) || 0)
      .curve(d3.curveMonotoneX);

    // Draw lines and points for each series
    series.forEach(seriesItem => {
      const seriesLine = line.y(d => y(Number(d[seriesItem.key])));

      // Draw line
      svg.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', seriesItem.color)
        .attr('stroke-width', 2)
        .attr('d', seriesLine);

      // Draw points only if showDots is not explicitly set to false
      const shouldShowDots = seriesItem.showDots !== false;
      if (shouldShowDots) {
        svg.selectAll(`.dot-${seriesItem.key}`)
          .data(data)
          .enter()
          .append('circle')
          .attr('class', `dot-${seriesItem.key}`)
          .attr('cx', d => x(String(d[xAxisKey])) || 0)
          .attr('cy', d => y(Number(d[seriesItem.key])))
          .attr('r', 4)
          .attr('fill', seriesItem.color)
          .attr('stroke', 'white')
          .attr('stroke-width', 2);
      }
    });

    const xValues = data.map(d => String(d[xAxisKey]));
    const xPos = xValues.map(v => x(v) ?? 0);

    // Optional vertical guide
    const guide = svg.append('line')
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#9FA1A4')
      .attr('stroke-width', 1)
      .style('opacity', 0);

    // Transparent overlay to capture mouse
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .on('mouseenter', () => {
        tooltip.style('opacity', 1);
        guide.style('opacity', 1);
      })
      .on('mouseleave', () => {
        tooltip.style('opacity', 0);
        guide.style('opacity', 0);
      })
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event, svg.node() as any);

        // find closest x index
        let idx = 0, best = Infinity;
        for (let i = 0; i < xPos.length; i++) {
          const d = Math.abs(mx - xPos[i]);
          if (d < best) { best = d; idx = i; }
        }
        const datum = data[idx];
        const label = String(datum[xAxisKey]);

        // build rows for all series
        const rows = series.map(s => {
          const val = Number(datum[s.key]);
          const name = s.label ?? s.key;
          const text = isNaN(val) ? '-' : tooltipFormat ? tooltipFormat(val) : this.transloco.translate('driverManager.duration.hours', { count: val });
          return `
            <div style="margin-top:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;
                           background:${s.color};margin-right:6px;"></span>
              <span style="color:#9FA1A4;font-size:12px;font-weight: 700;">${name}: ${text}</span>
            </div>`;
        }).join('');

        tooltip.html(`
          <div style="font-weight:700;margin-bottom:4px;color:#4D4749;">${label}</div>
          ${rows}
        `);

        // tooltip position relative to viewport (fixed positioning)
        const tooltipNode = tooltip.node() as HTMLElement;
        const tw = tooltipNode?.offsetWidth ?? 0;
        const th = tooltipNode?.offsetHeight ?? 0;

        let tooltipX = event.clientX + 10;
        let tooltipY = event.clientY - th - 10;

        // Prevent going off right edge
        if (tooltipX + tw > window.innerWidth - 8) {
          tooltipX = event.clientX - tw - 10;
        }
        // Prevent going off top edge
        if (tooltipY < 8) {
          tooltipY = event.clientY + 20;
        }
        // Prevent going off left edge
        if (tooltipX < 8) {
          tooltipX = 8;
        }

        tooltip.style('left', tooltipX + 'px')
               .style('top', tooltipY + 'px');

        // move guide
        guide.attr('x1', xPos[idx]).attr('x2', xPos[idx]);
      });
  }
}